'use client';

/**
 * PeriodComparison — Phase F2 Sprint 2.
 *
 * Companion to TimeRangePicker that toggles a "vs previous period"
 * comparison view. Closes the Phase C audit gap: 0/6 dashboards
 * supported period-over-period comparison.
 *
 * The widget itself is a small segmented toggle ('Off' | 'Previous
 * period' | 'Previous year'). Computing the comparison range is the
 * separate job of `resolveComparisonRange()` — given a base TimeRange
 * + the comparison mode, it returns the same-shape comparison range.
 *
 * Consumer flow:
 *   const [comparison, setComparison] = useState<ComparisonMode>('off');
 *   <PeriodComparison value={comparison} onChange={setComparison} />
 *   const compRange = resolveComparisonRange(baseRange, comparison);
 *   // Run the same query against compRange + render deltas in the UI.
 */

import React from 'react';
import { cn } from '../../lib/utils';
import type { TimeRange } from '../time-range-picker/time-range-picker';

export type ComparisonMode = 'off' | 'previous-period' | 'previous-year';

export interface PeriodComparisonProps {
  value: ComparisonMode;
  onChange: (next: ComparisonMode) => void;
  className?: string;
}

const COMPARISON_OPTIONS: ReadonlyArray<{ id: ComparisonMode; label: string }> = [
  { id: 'off', label: 'No comparison' },
  { id: 'previous-period', label: 'Previous period' },
  { id: 'previous-year', label: 'Previous year' },
];

export function PeriodComparison({ value, onChange, className }: PeriodComparisonProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Period comparison"
      className={cn(
        'inline-flex h-8 items-center rounded-md border border-slate-200 bg-slate-50 p-0.5 text-[11.5px]',
        className,
      )}
      data-period-comparison
    >
      {COMPARISON_OPTIONS.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.id)}
            className={cn(
              'rounded px-2 py-0.5 font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible',
              active
                ? 'bg-white text-statura-ink shadow-sm'
                : 'text-text-muted hover:text-statura-ink',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Given a base TimeRange + a comparison mode, return the comparison range.
 *
 * Modes:
 *   - 'off' → null (no comparison query needed)
 *   - 'previous-period' → shift back by the base range's duration
 *       e.g. base = last 30 days → prev = the 30 days before that
 *   - 'previous-year' → shift back by 365 days
 *
 * UTC-anchored ms arithmetic only (no .setMonth/.setFullYear) to comply
 * with the Statura lint rule that bans browser-local Date methods.
 */
export function resolveComparisonRange(base: TimeRange, mode: ComparisonMode): TimeRange | null {
  if (mode === 'off') return null;
  const DAY = 24 * 60 * 60 * 1000;
  const YEAR = 365 * DAY;

  function shiftCustom(from?: string, to?: string, deltaMs?: number): TimeRange {
    if (!from || !to || !deltaMs) {
      return { preset: 'custom', from, to };
    }
    const f = new Date(from + 'T00:00:00.000Z').getTime() - deltaMs;
    const t = new Date(to + 'T00:00:00.000Z').getTime() - deltaMs;
    return {
      preset: 'custom',
      // eslint-disable-next-line statura/no-utc-date-string -- inputs are already UTC-anchored YYYY-MM-DD strings; we deliberately emit the same UTC-anchored format here so resolveTimeRangeToIso can later re-apply tenant-tz bounds. Not a "today" calculation.
      from: new Date(f).toISOString().slice(0, 10),
      // eslint-disable-next-line statura/no-utc-date-string -- see above
      to: new Date(t).toISOString().slice(0, 10),
    };
  }

  if (mode === 'previous-year') {
    if (base.preset === 'custom') return shiftCustom(base.from, base.to, YEAR);
    // Non-custom presets — emit a 'custom' range matching the base
    // window but shifted back 1 year. The consumer can then resolve
    // it via resolveTimeRangeToIso.
    const now = new Date();
    const days = presetDays(base.preset);
    if (days == null) return null;
    const baseStartMs = now.getTime() - days * DAY;
    const prevEnd = new Date(now.getTime() - YEAR);
    const prevStart = new Date(baseStartMs - YEAR);
    return {
      preset: 'custom',
      // eslint-disable-next-line statura/no-utc-date-string -- emitting UTC-anchored YYYY-MM-DD for downstream resolveTimeRangeToIso to re-bound in tenant tz; not a "today" calculation.
      from: prevStart.toISOString().slice(0, 10),
      // eslint-disable-next-line statura/no-utc-date-string -- see above
      to: prevEnd.toISOString().slice(0, 10),
    };
  }

  // 'previous-period'
  if (base.preset === 'custom') {
    if (!base.from || !base.to) return null;
    // Pass-1 P2 (round 2): compute span as INCLUSIVE day count (from→to
    // inclusive) so the previous window has the same number of calendar
    // days as the base + is non-overlapping. Base 2026-05-01..2026-05-10
    // is 10 days inclusive → prev = 2026-04-21..2026-04-30 (also 10
    // days). Previously shifted using ms-span which produced an 11-day
    // result after the boundary fix.
    const fromMs = new Date(base.from + 'T00:00:00.000Z').getTime();
    const toMs = new Date(base.to + 'T00:00:00.000Z').getTime();
    const inclusiveDays = Math.round((toMs - fromMs) / DAY) + 1;
    return shiftCustom(base.from, base.to, inclusiveDays * DAY);
  }
  const days = presetDays(base.preset);
  if (days == null) return null;
  const now = new Date();
  const baseStartMs = now.getTime() - days * DAY;
  const prevStart = new Date(baseStartMs - days * DAY);
  const prevEnd = new Date(baseStartMs - DAY);
  return {
    preset: 'custom',
    // eslint-disable-next-line statura/no-utc-date-string -- emitting UTC-anchored YYYY-MM-DD for downstream resolveTimeRangeToIso to re-bound in tenant tz; not a "today" calculation.
    from: prevStart.toISOString().slice(0, 10),
    // eslint-disable-next-line statura/no-utc-date-string -- see above
    to: prevEnd.toISOString().slice(0, 10),
  };
}

function presetDays(preset: TimeRange['preset']): number | null {
  switch (preset) {
    case 'today':
      return 1;
    case '7d':
      return 7;
    case '30d':
      return 30;
    case '90d':
      return 90;
    case '12m':
      return 365;
    default:
      return null;
  }
}

/**
 * Compute a percentage delta + tone for displaying metric comparisons.
 * Returns `{ pct, tone }`: tone is 'up' when delta > 0, 'down' when
 * delta < 0, 'neutral' when delta is exactly 0 OR previous was zero
 * (can't compute %).
 */
export function comparisonDelta(
  current: number,
  previous: number,
): { pct: number | null; tone: 'up' | 'down' | 'neutral' } {
  if (previous === 0 || !Number.isFinite(previous) || !Number.isFinite(current)) {
    return { pct: null, tone: 'neutral' };
  }
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  if (Math.abs(pct) < 0.05) return { pct: 0, tone: 'neutral' };
  return { pct, tone: pct > 0 ? 'up' : 'down' };
}
